import React, { useMemo, useRef } from 'react';
import {
  EXPRESSION_MAP,
  INTERVIEW_BACKGROUND_COLOR,
  resolveAvatarPresentation,
} from './avatarBehavior';
import { useAvatarAnimator } from './useAvatarAnimator';
import ReactiveAudioWaveform from './ReactiveAudioWaveform';
import DavidSpeechTranscript from './DavidSpeechTranscript';
import './AIInterviewer.css';

/**
 * Modern AI Interviewer stage presenting clean conversational states:
 * - David Speaking (Amber glow + Speaking pill + TTS waveform)
 * - Candidate Listening (Indigo glow + Listening pill + Mic waveform + "I'm done" button)
 * - Thinking (Indigo glow + Thinking pill + resting waveform — NO processing text/buttons)
 */
export function AIInterviewer({
  raniSpeaking = false,
  showingClosingTTS = false,
  processingAnswer = false,
  waitingForAnswer = false,
  isRecording = false,
  questionType = '',
  videoUrl = null,
  onVideoEnded = null,
  audioElement = null,
  fallbackImageUrl = null,
  statusLabel = null,
  davidAnalyser = null,
  micAnalyser = null,
  readyToSubmit = false,
  onSilenceDone = null,
  onSubmitAnswer = null,
  isWhisperLoading = false,
  recordingSecondsLeft = null,
  idleSeconds = 0,
  speechTranscriptText = null,
}) {
  const avatarRef = useRef(null);

  const { behavior, expression } = useMemo(
    () =>
      resolveAvatarPresentation({
        raniSpeaking,
        showingClosingTTS,
        processingAnswer: processingAnswer || readyToSubmit || isWhisperLoading,
        waitingForAnswer,
        isRecording,
        questionType,
      }),
    [
      raniSpeaking,
      showingClosingTTS,
      processingAnswer,
      readyToSubmit,
      isWhisperLoading,
      waitingForAnswer,
      isRecording,
      questionType,
    ]
  );

  useAvatarAnimator({
    behavior,
    avatarRef,
    audioElement: raniSpeaking || showingClosingTTS ? audioElement : null,
  });

  const expressionSrc = EXPRESSION_MAP[expression] || EXPRESSION_MAP.introduction;
  const imgSrc = fallbackImageUrl || expressionSrc;

  // Conversational state mapping:
  const isDavidSpeaking = raniSpeaking || showingClosingTTS;
  const isThinking = !isDavidSpeaking && (processingAnswer || readyToSubmit || isWhisperLoading);
  const isCandidateListening = !isDavidSpeaking && !isThinking && (waitingForAnswer || isRecording);

  // Active Web Audio analyser node:
  const activeAnalyser = isDavidSpeaking
    ? davidAnalyser
    : (isCandidateListening ? micAnalyser : null);

  const waveformMode = isDavidSpeaking ? 'speaking' : (isCandidateListening ? 'listening' : 'idle');
  const isWaveformActive = isDavidSpeaking || isCandidateListening;

  // Status pill display text:
  // - Speaking (intro, questions, closing) ALWAYS shows "Speaking" (or closing status)
  // - Listening ALWAYS shows "Listening"
  // - Thinking shows "Thinking about your answer…", and ONLY IF prolonged does it show statusLabel
  let label = 'Ready';
  if (isDavidSpeaking) {
    label = (showingClosingTTS && statusLabel) ? statusLabel : 'Speaking';
  } else if (isThinking) {
    label = statusLabel || 'Thinking about your answer…';
  } else if (isCandidateListening) {
    label = 'Listening';
  } else if (statusLabel) {
    label = statusLabel;
  }

  return (
    <div className="ai-interviewer-stage" aria-label="AI Interviewer Stage">
      {/* Ambient background container */}
      <div
        className="ai-interviewer-background"
        style={{ backgroundColor: INTERVIEW_BACKGROUND_COLOR }}
        aria-hidden="true"
      />

      {/* Center Conversational Cluster */}
      <div className="ai-interviewer-center-cluster">
        {/* Avatar Anchor with floating transcript positioned strictly above */}
        <div className="ai-interviewer-avatar-anchor">
          {/* Dynamic Word-Synced Live Speech Transcript on Top of David */}
          <DavidSpeechTranscript
            text={speechTranscriptText}
            audioElement={audioElement}
            isActive={Boolean(speechTranscriptText)}
          />

          {/* Circular Avatar */}
          <div
            className={`ai-interviewer-avatar-circle ${
              isDavidSpeaking
                ? 'ai-interviewer-avatar-circle--speaking'
                : isThinking
                ? 'ai-interviewer-avatar-circle--thinking'
                : isCandidateListening
                ? 'ai-interviewer-avatar-circle--listening'
                : ''
            }`}
          >
            {videoUrl ? (
              <video
                key={videoUrl}
                className="ai-interviewer-video-circle"
                src={videoUrl}
                autoPlay
                playsInline
                muted
                onEnded={() => {
                  if (typeof onVideoEnded === 'function') onVideoEnded();
                }}
              />
            ) : (
              <img
                ref={avatarRef}
                className={`ai-interviewer-avatar-img ai-interviewer-avatar-img--${expression}`}
                src={imgSrc}
                alt="David - AI Interviewer"
                draggable={false}
              />
            )}
          </div>
        </div>

        {/* Status Badge Pill */}
        <div
          className={`ai-status-pill ${
            isDavidSpeaking
              ? 'ai-status-pill--speaking'
              : isThinking
              ? 'ai-status-pill--thinking'
              : isCandidateListening
              ? 'ai-status-pill--listening'
              : 'ai-status-pill--idle'
          }`}
        >
          {isDavidSpeaking ? (
            <svg
              className="ai-status-icon ai-status-icon--speaking"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          ) : isThinking ? (
            <svg
              className="ai-status-icon ai-status-icon--thinking"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="14 14" />
            </svg>
          ) : isCandidateListening ? (
            <svg
              className="ai-status-icon ai-status-icon--listening"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          ) : (
            <span className="ai-status-dot" />
          )}
          <span className="ai-status-text">{label}</span>
        </div>

        {/* Hardware-Accelerated Audio Waveform */}
        <div className="ai-waveform-container">
          <ReactiveAudioWaveform
            analyserNode={activeAnalyser}
            mode={waveformMode}
            isActive={isWaveformActive}
            width={280}
            height={48}
            numBars={23}
          />
        </div>

        {/* Candidate Interactive Controls ONLY during active Listening */}
        {isCandidateListening && !isDavidSpeaking && !isThinking && (
          <div className="ai-candidate-controls">
            <div className="ai-listening-actions">
              {recordingSecondsLeft !== null && (
                <div
                  className={`ai-timer-chip ${
                    recordingSecondsLeft >= 90
                      ? 'is-warning'
                      : ''
                  }`}
                >
                  {String(Math.floor(recordingSecondsLeft / 60)).padStart(2, '0')}:
                  {String(recordingSecondsLeft % 60).padStart(2, '0')}
                </div>
              )}

              {typeof onSilenceDone === 'function' && (
                <button
                  type="button"
                  className="ai-done-btn"
                  onClick={onSilenceDone}
                  title="Finish speaking and submit your answer"
                >
                  I'm done
                </button>
              )}

              {idleSeconds >= 25 && (
                <div className="ai-idle-hint">
                  Speak your answer — it auto-submits when you finish, or click "I'm done"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIInterviewer;
